import type {
  DiscoveryCatalog, DiscoveryGuide, DiscoveryIndustry, DiscoverySolution, DiscoverySolutionGroup,
} from '@/types'

// ---------------------------------------------------------------------------
// The Builder Hub's ideas catalogue, as the start page, the ideas browser and
// fill-in-the-blanks all read it. The catalogue is DATA
// (GET /generateapp/discovery-catalog → backend/app/data/
// app_discovery_catalog.json); this file only indexes and searches it. Every
// pick hands the server catalogue IDS (DiscoveryGuide), never prompt text.
// ---------------------------------------------------------------------------

/** One idea to start from: a catalogue process, or a solution type's example app. */
export interface Idea {
  key: string
  title: string
  description: string
  whatYouGet: string[]
  area: string | null
  solution: string
  industries: string[] | '*'
  common: boolean
  guide: DiscoveryGuide
}

/** The user's business: a catalogue industry, or what they typed (id null). */
export interface BusinessChoice {
  id: string | null
  name: string
}

/** What starting from an idea writes into the hub. */
export interface HelpMeDecidePick {
  /** Starting description for the prompt (the user edits it). */
  text: string
  /** "Started from: …" note shown under the prompt. */
  from: string
  /** Catalogue ids — the server turns them into question guidance. */
  guide: DiscoveryGuide
  /** The solution type that builds it (for the "helps most" hint). */
  solution: DiscoverySolution | null
  /** The idea it came from, when there is one (fill-in-the-blanks needs it). */
  idea: Idea | null
}

export type IndustryInfo = DiscoveryIndustry & { sector: string }

export interface CatalogIndex {
  industries: Record<string, IndustryInfo>
  solutions: Record<string, DiscoverySolution>
  areas: Record<string, string>
  groups: Record<string, DiscoverySolutionGroup>
}

export function indexCatalog(cat: DiscoveryCatalog): CatalogIndex {
  const industries: Record<string, IndustryInfo> = {}
  const solutions: Record<string, DiscoverySolution> = {}
  const areas: Record<string, string> = {}
  const groups: Record<string, DiscoverySolutionGroup> = {}
  cat.sectors.forEach((s) => s.industries.forEach((i) => { industries[i.id] = { ...i, sector: s.name } }))
  cat.solutions.forEach((s) => { solutions[s.id] = s })
  cat.areas.forEach((a) => { areas[a.id] = a.name })
  ;(cat.solution_groups || []).forEach((g) => { groups[g.id] = g })
  return { industries, solutions, areas, groups }
}

/** Processes first, then each solution type's example apps (an example that
 *  repeats a process's name is the same idea and is skipped). */
export function buildIdeas(cat: DiscoveryCatalog): Idea[] {
  const ideas: Idea[] = cat.processes.map((p) => ({
    key: `p:${p.id}`,
    title: p.name,
    description: p.description,
    whatYouGet: p.what_you_get || [],
    area: p.area,
    solution: p.solution,
    industries: p.industries,
    common: !!p.common,
    guide: { process: p.id },
  }))
  const seen = new Set(ideas.map((i) => i.title.toLowerCase()))
  cat.solutions.forEach((s) => s.examples.forEach((e, j) => {
    if (seen.has(e.title.toLowerCase())) return
    seen.add(e.title.toLowerCase())
    ideas.push({
      key: `s:${s.id}:${j}`,
      title: e.title,
      description: e.description,
      whatYouGet: [],
      area: null,
      solution: s.id,
      industries: '*',
      common: false,
      guide: { solution: s.id, example: j },
    })
  }))
  return ideas
}

export const fitsBusiness = (idea: Idea, businessId: string | null): boolean => (
  !businessId || idea.industries === '*' || idea.industries.includes(businessId)
)

/** Made for this business specifically (not just "every business"). */
export const madeFor = (idea: Idea, businessId: string | null): boolean => (
  !!businessId && idea.industries !== '*' && idea.industries.includes(businessId)
)

/** Fewer industries = more specific to each of them. */
const specificity = (idea: Idea): number => (idea.industries === '*' ? 1000 : idea.industries.length)

/** Ideas most specific to the business first; stable within a tie. */
export const bySpecificity = (businessId: string | null) => (a: Idea, b: Idea): number => (
  Number(madeFor(b, businessId)) - Number(madeFor(a, businessId))
  || (businessId ? specificity(a) - specificity(b) : 0)
)

/** The start page's few ideas: those made for the business, most specific
 *  first; with no business (or none made for it) the catalogue's `common` ones. */
export function popularIdeas(ideas: Idea[], businessId: string | null, count = 5): Idea[] {
  if (businessId) {
    const own = ideas.filter((i) => madeFor(i, businessId)).sort(bySpecificity(businessId))
    if (own.length) return own.slice(0, count)
  }
  return ideas.filter((i) => i.common).slice(0, count)
}

/** Every whitespace-separated word must appear (case-insensitive). */
export const matcher = (q: string) => {
  const toks = q.toLowerCase().split(/\s+/).filter(Boolean)
  return (text: string): boolean => toks.every((t) => text.toLowerCase().includes(t))
}

export function ideaSearchText(idea: Idea, idx: CatalogIndex): string {
  const inds = idea.industries === '*'
    ? ''
    : idea.industries.map((i) => `${idx.industries[i]?.name || ''} ${idx.industries[i]?.keywords || ''}`).join(' ')
  return [
    idea.title, idea.description, idea.whatYouGet.join(' '),
    idea.area ? idx.areas[idea.area] || '' : '', idx.solutions[idea.solution]?.title || '', inds,
  ].join(' ')
}

const forBusiness = (business: BusinessChoice | null): string => (
  business ? ` for our ${business.name.toLowerCase()} business` : ''
)

/** Start from an idea: the starting description + the ids for the questions. */
export function pickFromIdea(idea: Idea, business: BusinessChoice | null, idx: CatalogIndex): HelpMeDecidePick {
  const covers = idea.whatYouGet.length ? ` It should cover: ${idea.whatYouGet.join('; ')}.` : ''
  return {
    text: `We want "${idea.title}"${forBusiness(business)}. ${idea.description}.${covers}`,
    from: `${idea.title}${business ? ` · ${business.name}` : ''}`,
    guide: { ...idea.guide, ...(business?.id ? { industry: business.id } : {}) },
    solution: idx.solutions[idea.solution] || null,
    idea,
  }
}

/** "Not sure at all?" — no idea picked, only (maybe) the business. */
export function unsurePick(business: BusinessChoice | null, typed: string): HelpMeDecidePick {
  const text = typed.trim().length >= 10
    ? typed.trim()
    : `We want an app${forBusiness(business)} but are not sure exactly what yet. Help us decide.`
  return {
    text,
    from: business ? business.name : 'Your answers',
    guide: { unsure: true, ...(business?.id ? { industry: business.id } : {}) },
    solution: null,
    idea: null,
  }
}

/** The blanks template as literal text and {slot} tokens, in order. */
export function parseTemplate(template: string): Array<{ text: string } | { slot: string }> {
  const parts: Array<{ text: string } | { slot: string }> = []
  const re = /\{(\w+)\}/g
  let last = 0
  let m: RegExpExecArray | null
  while ((m = re.exec(template)) !== null) {
    if (m.index > last) parts.push({ text: template.slice(last, m.index) })
    parts.push({ slot: m[1] })
    last = m.index + m[0].length
  }
  if (last < template.length) parts.push({ text: template.slice(last) })
  return parts
}

// ── remembered business (a per-browser convenience, never required) ──────
const businessKey = (userId: string | undefined) => `twasta.builder.business.${userId || 'anon'}`

export function loadBusiness(userId: string | undefined): BusinessChoice | null {
  try {
    const raw = localStorage.getItem(businessKey(userId))
    if (!raw) return null
    const v = JSON.parse(raw) as Partial<BusinessChoice>
    if (typeof v?.name !== 'string' || !v.name.trim()) return null
    return { id: typeof v.id === 'string' ? v.id : null, name: v.name.trim().slice(0, 120) }
  } catch {
    return null
  }
}

export function saveBusiness(userId: string | undefined, choice: BusinessChoice | null): void {
  try {
    if (choice) localStorage.setItem(businessKey(userId), JSON.stringify(choice))
    else localStorage.removeItem(businessKey(userId))
  } catch {
    // Storage unavailable (private window, blocked site data) — the choice
    // just isn't remembered; the page works the same.
  }
}
